// Copyright (c) Docugami, Inc. All rights reserved.

import { getTokenRangesForPages } from "sdv/Page";
import { SearchStateComponent } from "sdv/components/SearchStateComponent";
import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { createFocusViewEntity } from "sdv/engine/EntityFactory";
import { clampAndCreateChunkBounds } from "sdv/utils/TokenBounds";
import { Box } from "store/shared_types";
import { rangesOverlap } from "utils/MathUtils";
import { IResources } from "../Resources";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Transforms the externally provided search state into its component state used for rendering.
 */
export class SearchSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    queries: Queries = {
        searchState: { types: [ComponentId.SearchState] }
    }

    update() {
        if (!this.resources.check(['searchState', 'visiblePages', 'pagesLoaded'])) {
            return;
        }

        const searchState = this.resources.get('searchState').value;
        const searchEntity = this.queries.searchState.query!.single() ?? this.entities.create().addComponent<SearchStateComponent>({ typeId: ComponentId.SearchState, renderResults: {} });
        const searchStateComp = searchEntity.getComponent<SearchStateComponent>(ComponentId.SearchState)!;

        const isEnabled = !!searchState && searchState.results.length > 0;
        if (!isEnabled) {
            // Disable rendering
            searchStateComp.render = false;
        }
        else {
            // Enable rendering and create the render bounds for our restuls
            searchStateComp.render = true;

            const pageTokens = this.resources.get('pageTokens').value;
            const visiblePages = this.resources.get('visiblePages').value;
            const visibleRanges = getTokenRangesForPages(visiblePages);

            const newResults: { [key: string]: Box[] } = {};
            const oldSelectedKey = searchStateComp.selectedKey;
            delete searchStateComp.selectedKey;

            for (let i = 0; i < searchState!.results.length; ++i) {
                const result = searchState!.results[i];
                const isSelected = searchState!.currentIndex === i;

                const key = `${result.fromTok}.${result.toTok}`;
                if (isSelected) {
                    if (oldSelectedKey !== key) {
                        // The key has changed, let's ensure the range is visible
                        createFocusViewEntity(this.entities, result);
                    }

                    searchStateComp.selectedKey = key;
                }

                // Skip this result if it is not within the visible page ranges
                if (!visibleRanges.find(range => rangesOverlap(result.fromTok, result.toTok, range.fromTok, range.toTok))) {
                    continue;
                }

                let bounds = searchStateComp.renderResults[key] ?? clampAndCreateChunkBounds(result.fromTok, result.toTok, pageTokens, visiblePages);

                if (!!bounds?.length) {
                    newResults[key] = bounds;
                }
            }

            searchStateComp.renderResults = newResults;
        }
    }
}
